<template>
    <template v-if="profileQualificationStore.inEditMode">
        <MultiSelect
            :model-value="qualificationUpdateForm.selectedProfessionalBackgrounds"
            :options="allProfessionalBackgroundsOptions"
            filter
            option-label="label"
            class="joy-p-multiselect"
            :show-toggle-all="false"
            display="chip"
            data-key="id"
            append-to="self"
            :filter-placeholder="$t('profile.vetting.qualification.professional.background.search.placeholder')"
            :scroll-height="'500px'"
            data-testid="vetting-professional-background-edition"
            @change="updateProfessionalBackgrounds"
        >
            <template #value="modelValue">
                <div v-if="modelValue.value && modelValue.value.length > 0" class="professional-background-tags">
                    <VJoyTag
                        v-for="option of modelValue.value"
                        :key="option.id"
                        :label="option.label"
                        variant="secondary"
                        removable
                        @remove="handleOptionRemoved(option)"
                    />
                </div>
            </template>
            <template #option="value">
                <div class="professional-background-element">
                    <span>{{ value.option.label }}</span>
                    <span>{{ value.option.category }}</span>
                </div>
            </template>
        </MultiSelect>
    </template>
    <div
        v-for="selected in qualificationUpdateForm.selectedProfessionalBackgrounds"
        v-else
        :key="selected.id"
        data-testid="vetting-professional-background-list"
    >
        <VJoyTag :label="selected.label" variant="secondary" :data-testid="`vetting-professional-background-list-element__${selected.label}`" />
    </div>
</template>

<script setup lang="ts">
    import {computed} from 'vue';
    import {storeToRefs} from 'pinia';
    import {VJoyTag} from '@maltjoy/core-vue';
    import type {ProfessionalBackground} from '@api';
    import {useProfileQualificationStore} from '~/store/profileQualificationStore';

    const profileQualificationStore = useProfileQualificationStore();
    const {allProfessionalBackgrounds, qualificationUpdateForm} = storeToRefs(profileQualificationStore);

    const allProfessionalBackgroundsOptions = computed(() =>
        (allProfessionalBackgrounds.value as ProfessionalBackground[]).map((bg): ProfessionalBackground => ({...bg})),
    );

    function updateProfessionalBackgrounds(event: any) {
        profileQualificationStore.qualificationUpdateForm.selectedProfessionalBackgrounds = event.value;
    }

    function handleOptionRemoved(removed: ProfessionalBackground) {
        profileQualificationStore.qualificationUpdateForm.selectedProfessionalBackgrounds =
            profileQualificationStore.qualificationUpdateForm.selectedProfessionalBackgrounds.filter((it) => it.id !== removed.id);
    }
</script>

<style lang="scss" scoped>
    .professional-background-tags {
        display: contents;
    }

    .professional-background-element {
        width: 100%;
        display: flex;
        justify-content: space-between;
    }

    .joy-p-multiselect-2 {
        background-color: var(--joy-color-error-50);

        &.p-multiselect {
            background-color: var(--joy-color-error-50);
            position: relative;
            width: 100%;
            border: var(--joy-form-field-border-width) solid var(--joy-color-neutral-30);
            border-radius: var(--joy-core-radius-3);

            &:hover {
                border-color: var(--joy-color-secondary-50);
            }
        }

        .p-overlay-open {
            border: var(--joy-form-field-border-width) solid var(--joy-color-secondary-50);
        }

        .p-multiselect-label {
            background-color: var(--joy-color-error-50);
            background-color: var(--joy-color-error-50);
            display: flex !important;
            flex-wrap: wrap;
            row-gap: var(--joy-core-spacing-3);
        }

        .p-multiselect-label-container {
            padding: var(--joy-core-spacing-3) var(--joy-core-spacing-5);
        }

        .p-placeholder {
            line-height: 32px;
            font-size: var(--joy-font-size-primary-300);
            color: var(--joy-color-neutral-50);
        }

        .p-multiselect-trigger {
            padding: 0 var(--joy-core-spacing-4);
            color: var(--joy-color-secondary-50);
        }

        .p-multiselect-chevron {
            transition: all 275ms;
        }

        .p-multiselect-chevron-top {
            transform: rotate(0.5turn);
        }

        .p-multiselect-panel {
            border: var(--joy-form-field-border-width) solid var(--joy-color-secondary-50);
            border-radius: var(--joy-core-radius-3);
            background: var(--joy-color-neutral-0);
            position: absolute;
            top: calc(100% + var(--joy-core-spacing-4)) !important;
            width: 100%;
            box-shadow: var(--joy-core-elevation-3);
        }

        .p-multiselect-option,
        .p-multiselect-empty-message {
            font-size: var(--joy-font-size-primary-300);
            padding: var(--joy-core-spacing-4) var(--joy-core-spacing-5);
        }

        .p-multiselect-option {
            &:hover {
                background: var(--joy-color-secondary-10);
            }

            &:first-child:hover {
                border-radius: var(--joy-core-radius-3) var(--joy-core-radius-3) 0 0;
            }

            &:last-child:hover {
                border-radius: 0 0 var(--joy-core-radius-3) var(--joy-core-radius-3);
            }

            &.p-highlight {
                color: var(--joy-color-secondary-50);
                font-weight: var(--joy-font-weight-bold);
            }
        }
    }
</style>
